import {useLayoutEffect, useRef} from 'react';

/**
 * Equalizes the height of every element matching `selector` within the returned container ref to
 * the tallest one, so rows of cards line up even when their content differs (e.g. plan highlight
 * lists with different line counts). Re-measures after web fonts load and on container resize, so
 * the reserved height is correct before any interaction triggers a re-render — this prevents the
 * layout-shift/jump that happens when a card's height is first measured only on selection.
 */
export function useEqualizeHeights<T extends HTMLElement>(selector: string, deps: unknown[]) {
  const containerRef = useRef<T>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const equalize = () => {
      const nodes = Array.from(container.querySelectorAll<HTMLElement>(selector));
      if (nodes.length === 0) return;
      for (const node of nodes) node.style.minHeight = '';
      const max = Math.max(...nodes.map((node) => node.offsetHeight));
      for (const node of nodes) node.style.minHeight = `${max}px`;
    };

    equalize();

    // Web fonts can finish loading after the first measurement, changing line
    // wrapping and row heights. Re-equalize once they're ready so the reserved
    // height is correct before any interaction triggers a re-render.
    let cancelled = false;
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) equalize();
      });
    }

    let lastWidth = container.offsetWidth;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.offsetWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      equalize();
    });
    observer.observe(container);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return containerRef;
}
